import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [cart, setCart] = useState({ items: [], subtotal: 0 });
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(false);

  const refreshCart = useCallback(async () => {
    if (!user) {
      setCart({ items: [], subtotal: 0 });
      return;
    }
    try {
      const { data } = await api.get("/cart");
      setCart(data);
    } catch {}
  }, [user]);

  const refreshWishlist = useCallback(async () => {
    if (!user) {
      setWishlist([]);
      return;
    }
    try {
      const { data } = await api.get("/wishlist");
      setWishlist(data.items || []);
    } catch {}
  }, [user]);

  useEffect(() => {
    refreshCart();
    refreshWishlist();
  }, [refreshCart, refreshWishlist]);

  const addToCart = async (productId, quantity = 1) => {
    if (!user) return { ok: false, requireLogin: true };
    setLoading(true);
    try {
      const { data } = await api.post("/cart/add", { product_id: productId, quantity });
      setCart(data);
      return { ok: true };
    } finally {
      setLoading(false);
    }
  };

  const updateCart = async (productId, quantity) => {
    const { data } = await api.post("/cart/update", { product_id: productId, quantity });
    setCart(data);
  };

  const removeFromCart = async (productId) => {
    const { data } = await api.post("/cart/remove", { product_id: productId, quantity: 1 });
    setCart(data);
  };

  const clearCart = async () => {
    const { data } = await api.post("/cart/clear");
    setCart(data);
  };

  const toggleWishlist = async (productId) => {
    if (!user) return { ok: false, requireLogin: true };
    const { data } = await api.post("/wishlist/toggle", { product_id: productId, quantity: 1 });
    refreshWishlist();
    return { ok: true, in_wishlist: data.in_wishlist };
  };

  const itemCount = cart.items?.reduce((s, it) => s + it.quantity, 0) || 0;
  const inWishlist = (pid) => wishlist.some((p) => p.id === pid);

  return (
    <CartContext.Provider
      value={{ cart, wishlist, loading, itemCount, inWishlist, addToCart, updateCart, removeFromCart, clearCart, toggleWishlist, refreshCart, refreshWishlist }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
